import type { View } from "@novorender/api";

type QueryName =
    | "getNormalAtPosition"
    | "getObjectTriangles"
    | "getObjectsOutlinePlaneBoundingRectInWorld"
    | "getOutlineObjectsOnScreen"
    | "outlineLaser"
    | "screenSpaceLaser"
    | "selectOutlinePoint"
    | "threePointLaser";

/**
 * Runs a synchronous `View` query without blocking the main thread where the engine allows it.
 *
 * The bim-viewer-engine api exposes a `<name>Async` twin of each of these queries that is answered by the
 * engine worker without busy-waiting this thread. The Novorender api only has the synchronous
 * method, which is cheap there, so it is used as is.
 */
export async function queryView<K extends QueryName>(
    view: View,
    name: K,
    ...args: Parameters<View[K]>
): Promise<Awaited<ReturnType<View[K]>>> {
    const asyncQuery = (view as unknown as Record<string, unknown>)[`${name}Async`];
    const query = typeof asyncQuery === "function" ? asyncQuery : view[name];
    return (await (query as (...args: unknown[]) => unknown).apply(view, args)) as Awaited<ReturnType<View[K]>>;
}

/**
 * Orders the results of overlapping async queries: of the requests that finish out of order,
 * only a newer one than the last accepted may still apply its result.
 */
export function createLatestGate() {
    let started = 0;
    let settled = 0;
    return {
        begin: () => ++started,
        /** Whether `ticket` is newer than every result applied or cancelled so far; claims it if so. */
        accept: (ticket: number) => {
            if (ticket <= settled) {
                return false;
            }
            settled = ticket;
            return true;
        },
        /** Drops every result of tickets up to and including `ticket`, e.g. when a newer state needs no query. */
        cancel: (ticket: number) => {
            settled = Math.max(settled, ticket);
        },
    };
}
